import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiPlus, FiSearch, FiCheckCircle, FiEye, FiFileText, FiTrash2, FiLock, FiUsers, FiRefreshCw } from 'react-icons/fi';
import AppLayout from '../components/layout/AppLayout';
import Button from '../components/common/Button';
import Chip from '../components/common/Chip';
import AddCompanyModal from '../components/common/AddCompanyModal';
import McaRefreshModal from '../components/common/McaRefreshModal';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { ROUTES } from '../constants/constants';
import { useUI } from '../context/UIContext';
import { useCompanyDeadlines } from '../hooks/useCompanyDeadlines';
import { markOnboardingStep } from '../utils/onboarding';
import EmptyState from '../components/common/EmptyState';
import HoverPopover from '../components/common/HoverPopover';
import { IlloPortfolio, IlloSearch } from '../components/common/Illustrations';

/* ── Presentation helpers ─────────────────────────────────────────────── */
function statusChip(status) {
  const n = String(status || '').toLowerCase();
  if (n.includes('struck off') || n.includes('strike off') || n.includes('dissolved') ||
      n.includes('liquidated') || n.includes('defunct') || n.includes('under liquidation')) {
    return { label: 'Non-Compliant', bg: '#f2e9e6', color: '#9a564a' };
  }
  if (n === 'active') return { label: 'Active', bg: '#e8efe5', color: '#4a6b3f' };
  if (n.includes('inactive') || n.includes('under process') || n.includes('dormant') ||
      n.includes('under cirp') || n.includes('converted') || n.includes('amalgamated') ||
      n.includes('not available')) {
    return { label: 'Inactive', bg: '#f3ebd7', color: '#8a6a1f' };
  }
  return { label: 'Unknown', bg: '#f2eee6', color: '#8a7d66' };
}

function classifyType(type) {
  const t = String(type || '').toLowerCase();
  if (t.includes('llp')) return 'llp';
  if (t.includes('public')) return 'public';
  if (t.includes('opc') || t.includes('one person')) return 'opc';
  if (t.includes('sec 8')) return 'sec8';
  if (t.includes('foreign') || t.includes('nidhi') || t.includes('producer') || t.includes('government')) return 'other';
  return 'pvt';
}

function typeTone(type) {
  const t = String(type || '').toLowerCase();
  if (t.includes('llp')) return '#145f5f';
  if (t.includes('public')) return '#9a564a';
  if (t.includes('sec 8') || t.includes('ngo')) return '#4a6b3f';
  if (t.includes('nidhi') || t.includes('producer') || t.includes('government')) return '#8a6a1f';
  return '#3a4a66';
}

function companyRelationship(company, myId) {
  const ownerId = String(company?.owner?._id || company?.owner || '');
  const creatorId = String(company?.createdBy?._id || company?.createdBy || '');
  if (ownerId && ownerId === myId) return 'owner';
  if (!ownerId && creatorId && creatorId === myId) return 'creator';
  return 'shared';
}

// What you may actually DO on a company someone else owns. The list endpoint
// returns `myAccessLevel` ('read' | 'read_write' | 'admin'); treat a missing
// value as read-only so the card never promises an edit the server will refuse.
//
// Deliberately SOLID-filled rather than the usual soft tint: every other badge
// on the card is a pale pill, so a tinted "Shared" vanished into the row. This
// is the one thing you must notice before acting on a company you don't own.
const ACCESS_CHIPS = {
  read: { label: 'Read only', bg: '#5a5145', Icon: FiLock, title: 'You can view this company but not edit it — ask the owner for edit access' },
  read_write: { label: 'Can edit', bg: '#3a4a66', Icon: FiUsers, title: 'Shared with you — you can view and edit this company' },
  admin: { label: 'Full access', bg: '#145f5f', Icon: FiUsers, title: 'Shared with you — you have full access to this company' },
};

const accessChip = (company) => ACCESS_CHIPS[company?.myAccessLevel] || ACCESS_CHIPS.read;

function RelationshipBadges({ company, relationship, claimed }) {
  if (relationship === 'shared') {
    const { label, bg, Icon, title } = accessChip(company);
    return (
      <Chip
        size="xs"
        bg={bg}
        color="#fff"
        title={title}
        className="gap-1 shadow-[0_1px_3px_rgba(8,17,31,0.18)]"
      >
        <Icon size={11} /> Shared · {label}
      </Chip>
    );
  }
  return (
    <>
      <Chip size="xs" bg="#e4f5f3" color="#145f5f" title="You own this company">Owner</Chip>
      {relationship === 'owner' && claimed && <Chip size="xs" bg="#f7eccf" color="#8a6d1f" title="You claimed this company">Claimed</Chip>}
      {relationship === 'creator' && <Chip size="xs" bg="#f3ebd7" color="#8a6a1f" title="Not yet claimed">Unclaimed</Chip>}
    </>
  );
}

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' }) : '-');

/* ── Icon action button with a tooltip (shared HoverPopover pill) ─────── */
function ActionBtn({ onClick, tooltip, active, children }) {
  return (
    <HoverPopover variant="tooltip" content={tooltip}>
      <button
        onClick={onClick}
        className="w-[30px] h-[30px] grid place-items-center rounded-lg transition-all duration-200 active:scale-90"
        style={{ background: active ? '#145f5f' : 'transparent', color: active ? '#fff' : '#7a7060' }}
        onMouseEnter={(e) => { if (!active) { e.currentTarget.style.background = '#faf7f2'; e.currentTarget.style.color = '#08111f'; } }}
        onMouseLeave={(e) => { e.currentTarget.style.background = active ? '#145f5f' : 'transparent'; e.currentTarget.style.color = active ? '#fff' : '#7a7060'; }}
      >
        {children}
      </button>
    </HoverPopover>
  );
}

/* ── A single company card ────────────────────────────────────────────── */
// Whether this user may write to the company — drives which card actions show.
// Falls back to the ownership relationship when the API predates myAccessLevel.
const canWrite = (company, rel) =>
  (company?.myAccessLevel
    ? ['read_write', 'admin'].includes(company.myAccessLevel)
    : rel !== 'shared');

function CompanyCard({ company, isActive, rel, score, cd, deleting, onSetActive, onEdit, onFiling, onDelete, onRefreshMca }) {
  const next = cd?.nextCritical;
  const nextLabel = next ? `${next.code} ${next.dueDateFormatted}` : null;
  const isOverdue = next?.status === 'overdue';
  const agmNotSet = cd?.agmNotSet;
  const agmNotSetForCurrentFy = cd?.agmNotSetForCurrentFy;
  const st = statusChip(company.status);
  const tone = typeTone(company.type);
  const scoreColor = score >= 80 ? '#4a6b3f' : score >= 50 ? '#8a6a1f' : '#9a564a';
  const location = company.city && company.state && company.city.toLowerCase() !== company.state.toLowerCase()
    ? `${company.city}, ${company.state}`
    : company.city || company.state || '';

  return (
    <div
      className="bg-white rounded-[16px] border p-4 h-full flex flex-col transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] hover:shadow-[0_12px_28px_-12px_rgba(122,112,96,0.18)]"
      style={{
        borderColor: isActive ? 'rgba(20,95,95,0.45)' : 'rgba(226,217,200,0.6)',
        background: isActive ? '#f8fcfb' : '#fff',
        boxShadow: 'var(--sh-1)',
        opacity: deleting ? 0.4 : 1,
      }}
    >
      {/* Identity + status */}
      <div className="flex items-start justify-between gap-2">
        <h3 className="font-bold text-[13px] leading-snug text-[#08111f] flex-1 min-w-0 break-words">{company.name}</h3>
        <Chip bg={st.bg} color={st.color}>{st.label}</Chip>
      </div>

      {/* Badges */}
      <div className="flex items-center gap-1.5 flex-wrap mt-2">
        <RelationshipBadges company={company} relationship={rel} claimed={Boolean(company.claimedAt)} />
        {isActive && <Chip size="xs" bg="#e4f5f3" color="#145f5f">Active workspace</Chip>}
      </div>

      {/* Meta */}
      <div className="flex items-center gap-2 mt-2.5 text-[11px] text-[#7a7060] flex-wrap">
        <Chip tone={tone}>{company.type}</Chip>
        <span className="text-[#5a5145]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{company.cin}</span>
      </div>
      <div className="text-[11px] text-[#7a7060] mt-1 truncate">Inc. {fmtDate(company.incorporated)}{location ? ` · ${location}` : ''}</div>

      {/* Compliance + next due */}
      <div className="mt-3 pt-3 border-t grid grid-cols-2 gap-3" style={{ borderColor: 'rgba(226,217,200,0.5)' }}>
        <div>
          <div className="text-[9px] uppercase tracking-[0.6px] text-[#7a7060] font-semibold mb-1.5">Compliance</div>
          <div className="flex items-center gap-2">
            <span className="text-[13px] font-bold tabular-nums" style={{ color: scoreColor }}>{score}%</span>
            <div className="flex-1 h-[4px] rounded-full overflow-hidden" style={{ background: '#f0ece2' }}>
              <div className="h-full rounded-full transition-all duration-500" style={{ width: `${score}%`, background: scoreColor }} />
            </div>
          </div>
        </div>
        <div className="min-w-0">
          <div className="text-[9px] uppercase tracking-[0.6px] text-[#7a7060] font-semibold mb-1.5">Next due</div>
          <div className="text-[12px] font-semibold truncate" style={{ color: isOverdue ? '#9a564a' : '#08111f' }}>{nextLabel || '—'}</div>
        </div>
      </div>

      {/* Footer: AGM + actions */}
      <div className="flex items-center justify-between gap-2 mt-auto pt-3">
        <div className="min-w-0">
          {(agmNotSet || agmNotSetForCurrentFy) && (
            <Chip size="xs" bg="#f3ebd7" color="#8a6a1f">{agmNotSet ? 'AGM missing' : 'AGM outstanding'}</Chip>
          )}
        </div>
        <div className="flex items-center gap-[2px] shrink-0">
          <ActionBtn onClick={onSetActive} tooltip={isActive ? 'Active workspace' : 'Set as active company'} active={isActive}><FiCheckCircle size={15} /></ActionBtn>
          <ActionBtn onClick={onEdit} tooltip="View company details"><FiEye size={15} /></ActionBtn>
          {/* Labelled, unlike its icon-only neighbours: "↻" alone reads as
              "reload the page", giving no hint that it re-fetches this
              company's data from the MCA registry. The word carries the
              meaning; the tooltip explains the review step. */}
          {canWrite(company, rel) && (
            <HoverPopover
              variant="tooltip"
              content="Re-fetch this company's details from the MCA registry — you review each change before anything is saved"
            >
              <button
                onClick={onRefreshMca}
                className="inline-flex items-center gap-1 h-[30px] px-2 rounded-lg text-[11px] font-semibold transition-all duration-200 active:scale-95"
                style={{ color: '#145f5f', background: 'rgba(20,95,95,0.08)' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(20,95,95,0.16)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(20,95,95,0.08)'; }}
              >
                <FiRefreshCw size={13} /> MCA
              </button>
            </HoverPopover>
          )}
          <ActionBtn onClick={onFiling} tooltip="Annual filing & compliance"><FiFileText size={15} /></ActionBtn>
          <ActionBtn onClick={onDelete} tooltip="Remove company"><FiTrash2 size={15} /></ActionBtn>
        </div>
      </div>
    </div>
  );
}

export default function CompaniesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { companies, activeCompany, selectCompany, deleteCompany, openAddCompanyModal, fetchCompanies } = useApp();
  const { showToast } = useUI();
  const { user } = useAuth();
  const myId = String(user?.id || user?._id || '');
  const { getDeadlinesFor, stats: deadlineStats } = useCompanyDeadlines();

  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [editingCompany, setEditingCompany] = useState(null);
  // Which profile section to open on, when arriving from a "fill this in" link.
  const [editSection, setEditSection] = useState(null);
  const [mcaCompany, setMcaCompany] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const counts = useMemo(() => {
    const byType = { all: companies.length, pvt: 0, llp: 0, public: 0, opc: 0, sec8: 0, other: 0 };
    companies.forEach((c) => { byType[classifyType(c.type)] += 1; });
    return byType;
  }, [companies]);

  const scoreFor = useMemo(() => {
    const map = {};
    companies.forEach((c) => {
      const cd = getDeadlinesFor(c);
      const items = cd ? [...cd.deadlines, ...(cd.agmDeadline ? [cd.agmDeadline] : [])] : [];
      map[c.cin] = items.length ? Math.round((items.filter((d) => d.status === 'compliant').length / items.length) * 100) : 0;
    });
    return map;
  }, [companies, getDeadlinesFor]);

  const avgScore = useMemo(() => {
    if (!companies.length) return 0;
    return Math.round(companies.reduce((a, c) => a + (scoreFor[c.cin] ?? 0), 0) / companies.length);
  }, [companies, scoreFor]);

  // Only surface search + filters once the portfolio is large enough to need them.
  const showControls = companies.length > 4;
  // Filter chips for the types that actually exist (plus All).
  const typeChips = useMemo(() => {
    const defs = [
      { id: 'pvt', label: 'Pvt Ltd' }, { id: 'public', label: 'Public' }, { id: 'llp', label: 'LLP' },
      { id: 'opc', label: 'OPC' }, { id: 'sec8', label: 'Sec 8' }, { id: 'other', label: 'Other' },
    ].filter((d) => counts[d.id] > 0);
    return [{ id: 'all', label: 'All' }, ...defs];
  }, [counts]);

  const filteredCompanies = useMemo(() => {
    const q = query.trim().toLowerCase();
    return companies.filter((c) => {
      const matchType = typeFilter === 'all' || classifyType(c.type) === typeFilter;
      const matchQuery = !q ||
        String(c.name || '').toLowerCase().includes(q) ||
        String(c.cin || '').toLowerCase().includes(q) ||
        String(c.city || '').toLowerCase().includes(q) ||
        String(c.state || '').toLowerCase().includes(q);
      return matchType && matchQuery;
    });
  }, [companies, query, typeFilter]);

  const confirmRel = confirmDelete ? companyRelationship(confirmDelete, myId) : null;

  // Getting-started runway: visiting completes "Explore client companies".
  useEffect(() => { markOnboardingStep('visited-companies'); }, []);

  useEffect(() => {
    if (location.state?.openAddCompany) openAddCompanyModal();
    if (location.state?.typeFilter) setTypeFilter(location.state.typeFilter);
    // Arriving from the compliance checklist's cannot-determine bucket: open
    // this company straight onto the section holding the field that would
    // settle it. Landing on the companies list and leaving the user to find it
    // is what makes "fill this in" advice go unfollowed.
    if (location.state?.editCompanyId) {
      const found = companies.find((c) => String(c._id || c.id) === String(location.state.editCompanyId));
      if (found) {
        setEditingCompany(found);
        setEditSection(location.state.profileSection || null);
        window.history.replaceState({}, '');
      }
    }
    if (location.state?.selectedCin) {
      const cin = location.state.selectedCin;
      const found = companies.find((c) => String(c.cin || '').toUpperCase() === String(cin).toUpperCase());
      if (found) {
        selectCompany(found);
        setTimeout(() => {
          const row = document.querySelector(`[data-cin="${String(cin).toUpperCase()}"]`);
          if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      }
      window.history.replaceState({}, '');
    }
  }, [location.state, openAddCompanyModal, companies, selectCompany]);

  // Slim header summary (replaces the redundant KPI cards).
  const summaryBits = [
    `${counts.all} ${counts.all === 1 ? 'entity' : 'entities'}`,
    companies.length ? `${avgScore}% avg compliance` : null,
    deadlineStats.overdue ? `${deadlineStats.overdue} need${deadlineStats.overdue === 1 ? 's' : ''} attention` : null,
  ].filter(Boolean);

  return (
    <AppLayout pageTitle="Companies" hasDashboardAlert={deadlineStats.overdue > 0}>
      <div className="mb-5">
        <h1 className="text-[24px] font-bold text-[#08111f]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Company Portfolio</h1>
        <p className="text-[12.5px] text-[#7a7060] mt-0.5">{summaryBits.join(' · ')}</p>
      </div>

      {/* Controls — only when the portfolio is large enough to warrant them */}
      {showControls && (
        <div className="flex items-center gap-2.5 mb-4 flex-wrap">
          <div className="relative flex-1 min-w-[240px] max-w-[340px]">
            <FiSearch size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7a7060] pointer-events-none" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search company, CIN, location…"
              className="w-full rounded-xl border-[1.5px] border-[#ddd6c6] bg-[#fdfbf7] pl-9 pr-3 h-[38px] text-[13px] text-[#08111f] outline-none transition-all duration-200 placeholder:text-[#b3a995] hover:border-[#cdc4b0] focus:border-[#145f5f] focus:bg-white focus:shadow-[0_0_0_3px_rgba(20,95,95,0.18)]"
            />
          </div>
          {typeChips.length > 2 && (
            <div className="flex items-center gap-1.5 flex-wrap">
              {typeChips.map((f) => {
                const on = typeFilter === f.id;
                return (
                  <button
                    key={f.id}
                    onClick={() => setTypeFilter(f.id)}
                    className={`group inline-flex items-center gap-1.5 px-3 h-[34px] rounded-lg text-[12px] font-semibold border-[1.5px] transition-all duration-200 active:scale-[0.97] ${
                      on ? 'bg-[#145f5f] text-white border-[#145f5f]' : 'bg-white text-[#7a7060] border-[#ddd6c6] hover:border-[#cdc4b0] hover:text-[#08111f]'
                    }`}
                  >
                    <span className="transition-transform duration-150 group-active:translate-y-[0.5px]">{f.label}</span>
                    <span className="text-[10px] font-bold px-1.5 py-[0.5px] rounded-full leading-none" style={on ? { background: 'rgba(255,255,255,0.22)', color: '#fff' } : { background: '#f0ece2', color: '#7a7060' }}>{counts[f.id]}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Company list */}
      {filteredCompanies.length === 0 ? (
        companies.length ? (
          <EmptyState
            variant="filter"
            illustration={<IlloSearch />}
            title="No matching companies"
            subtitle="Try a different search or clear the type filter."
            action={<Button variant="outline" size="sm" onClick={() => { setQuery(''); setTypeFilter('all'); }}>Clear filters</Button>}
          />
        ) : (
          <EmptyState
            variant="hero"
            illustration={<IlloPortfolio />}
            title="Build your compliance portfolio"
            subtitle="Add a company by CIN and we fetch everything from MCA — directors, status, AGM history — then start tracking every deadline and penalty automatically."
            action={<Button variant="gold" size="sm" className="!h-[38px] !rounded-xl" leftIcon={<FiPlus size={15} />} onClick={openAddCompanyModal}>Add your first company</Button>}
          />
        )
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {filteredCompanies.map((company) => (
            <div key={company.cin} data-cin={String(company.cin || '').toUpperCase()} className="h-full">
              <CompanyCard
                company={company}
                isActive={activeCompany?.cin === company.cin}
                rel={companyRelationship(company, myId)}
                score={scoreFor[company.cin] ?? 0}
                cd={getDeadlinesFor(company)}
                deleting={deletingId === (company._id || company.cin)}
                onSetActive={() => { selectCompany(company); showToast(`${company.name} set as active company`, 'success'); }}
                onEdit={() => setEditingCompany(company)}
                onRefreshMca={() => setMcaCompany(company)}
                onFiling={() => { selectCompany(company); navigate(ROUTES.ANNUAL_FILING); }}
                onDelete={() => setConfirmDelete(company)}
              />
            </div>
          ))}

          {/* Ghost card — completes the row and keeps "add another" one click away,
              only while viewing the whole (unfiltered) portfolio. */}
          {typeFilter === 'all' && !query && (
            <button
              onClick={openAddCompanyModal}
              className="group h-full min-h-[220px] rounded-[16px] border-[1.5px] border-dashed flex flex-col items-center justify-center gap-2 p-4 text-center transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] hover:bg-[#fdfaf1]"
              style={{ borderColor: 'rgba(184,151,58,0.32)' }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(184,151,58,0.62)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(184,151,58,0.32)'; }}
            >
              <span className="w-11 h-11 rounded-full grid place-items-center transition-transform duration-300 group-hover:scale-110" style={{ background: '#f7eccf', color: '#8a6a1f' }}>
                <FiPlus size={20} />
              </span>
              <span className="text-[13px] font-bold text-[#08111f]">Add another company</span>
              <span className="text-[11px] text-[#7a7060] max-w-[190px] leading-snug">Track deadlines &amp; penalties for one more entity — just its CIN.</span>
            </button>
          )}
        </div>
      )}

      <AddCompanyModal
        open={Boolean(editingCompany)}
        onClose={() => { setEditingCompany(null); setEditSection(null); }}
        initialData={editingCompany}
        openSection={editSection}
      />

      <McaRefreshModal
        open={Boolean(mcaCompany)}
        company={mcaCompany}
        onClose={() => setMcaCompany(null)}
        onApplied={fetchCompanies}
      />

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
        variant="danger"
        title={confirmRel === 'shared' ? 'Remove your access?' : 'Remove Company?'}
        message={confirmDelete ? (
          confirmRel === 'shared' ? (
            <>You'll lose access to <strong>{confirmDelete.name}</strong>. The owner and any other collaborators keep it.</>
          ) : (
            <>Confirming will remove <strong>{confirmDelete.name}</strong> from your portfolio.</>
          )
        ) : ''}
        note={confirmRel === 'shared'
          ? 'You can request access again later — this only removes your own access.'
          : 'MCA records remain unaffected — you can re-add this company anytime.'}
        confirmLabel={confirmRel === 'shared' ? 'Remove access' : 'Remove'}
        cancelLabel="Keep it"
        loading={Boolean(deletingId)}
        onConfirm={async () => {
          if (!confirmDelete) return;
          setDeletingId(confirmDelete._id || confirmDelete.cin);
          try {
            await deleteCompany(confirmDelete._id);
            showToast(confirmRel === 'shared' ? `Removed your access to ${confirmDelete.name}` : `${confirmDelete.name} removed`, 'success');
            setConfirmDelete(null);
          } catch (err) {
            showToast(err.message || 'Failed to remove company', 'error');
          } finally {
            setDeletingId(null);
          }
        }}
      />
    </AppLayout>
  );
}
